import { createContext, useContext, type ReactNode } from "react";
import type {
  BvaHostContextV1,
  BvaPermission,
} from "./host-contract";
import { getBvaScopeKey, hasBvaPermission } from "./host-contract";

interface MicroappRuntime {
  embedded: boolean;
  host: BvaHostContextV1 | null;
  standaloneAuthenticated: boolean;
}

const MicroappContext = createContext<MicroappRuntime>({
  embedded: false,
  host: null,
  standaloneAuthenticated: false,
});

export function MicroappContextProvider({
  host,
  children,
}: {
  host: BvaHostContextV1;
  children: ReactNode;
}) {
  return (
    <MicroappContext.Provider
      value={{ embedded: true, host, standaloneAuthenticated: false }}
    >
      {children}
    </MicroappContext.Provider>
  );
}

export function StandaloneAccessProvider({
  authenticated,
  children,
}: {
  authenticated: boolean;
  children: ReactNode;
}) {
  return (
    <MicroappContext.Provider
      value={{
        embedded: false,
        host: null,
        standaloneAuthenticated: authenticated,
      }}
    >
      {children}
    </MicroappContext.Provider>
  );
}

export function useMicroappContext(): MicroappRuntime {
  return useContext(MicroappContext);
}

export function useBvaPermission(permission: BvaPermission): boolean {
  const { embedded, host, standaloneAuthenticated } = useMicroappContext();
  return embedded
    ? Boolean(host && hasBvaPermission(host, permission))
    : standaloneAuthenticated;
}

export function useBvaStorageScope(): string {
  const { embedded, host } = useMicroappContext();
  if (!embedded || !host) return "standalone";
  let hash = 2166136261;
  for (const character of getBvaScopeKey(host)) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return `host-${(hash >>> 0).toString(36)}`;
}

export function PermissionGate({
  permission,
  children,
  fallback = null,
}: {
  permission: BvaPermission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return useBvaPermission(permission) ? <>{children}</> : <>{fallback}</>;
}